import { Box, Card, Divider, Grid, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useForm } from "react-hook-form";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import MDDatePicker from "components/MDDatePicker";
import { GenerateReportMutationVariables, ReportType } from "generated/graphql";
import { capitalCase } from "change-case";
import { useEffect, useMemo, useRef, useState } from "react";
import { kebabCase } from "lodash";
import useGenerateReport from "hooks/organization/reports/useGenerateReport";
import DataTable from "components/Tables/DataTable/DataTable";
import useReportTableTimeEntries from "hooks/organization/reports/types/useReportTableTimeEntries";
import dayjs from "dayjs";

const ReportTimeEntries = () => {
  const {
    handleSubmit,
    formState: { errors },
  } = useForm();

  let today = dayjs().format("YYYY-MM-DD 00:00:00");
  const [date, setDate] = useState({ startDate: today, endDate: today });
  const [generateReport, { data: generateReportData }] = useGenerateReport();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const {
    tableData,
    loading,
    data: reportTimeEntriesData,
    getReportTimeEntries,
  } = useReportTableTimeEntries(date.startDate, date.endDate);

  const [requestData, setRequestData] = useState<GenerateReportMutationVariables | null>(null);

  const downloadFileRef = useRef(null);

  // Create paginated table data
  const paginatedTableData = useMemo(() => {
    if (!tableData) return null;

    const originalTableData = tableData;
    const totalRows = originalTableData.rows?.length || 0;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    if (totalRows === 0) {
      return originalTableData;
    }

    const paginatedRows = originalTableData.rows.slice(startIndex, endIndex);

    return {
      ...originalTableData,
      rows: paginatedRows,
    };
  }, [tableData, currentPage, pageSize]);

  // Create pagination object for DataTable
  const pagination = useMemo(() => {
    if (!tableData || !tableData.rows) {
      return {
        currentPage: 1,
        setCurrentPage,
        pageSize,
        setPageSize,
        pageOptions: [],
        pages: 1,
        hasNextPage: false,
        hasPrevPage: false,
        nextPage: () => {},
        prevPage: () => {},
        total: 0,
        loadMore: () => {},
      };
    }

    const totalRows = tableData.rows.length;
    const totalPages = Math.ceil(totalRows / pageSize);
    const pageOptions = Array.from({ length: totalPages }, (_, i) => i + 1);

    return {
      currentPage,
      setCurrentPage,
      pageSize,
      setPageSize: (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when page size changes
      },
      pageOptions,
      pages: totalPages,
      hasNextPage: currentPage < totalPages,
      hasPrevPage: currentPage > 1,
      nextPage: () => setCurrentPage((prev) => Math.min(prev + 1, totalPages)),
      prevPage: () => setCurrentPage((prev) => Math.max(prev - 1, 1)),
      total: totalRows,
      loadMore: () => {},
    };
  }, [tableData, currentPage, pageSize]);

  // Reset pagination when report data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [reportTimeEntriesData]);

  useEffect(() => {
    if (generateReportData?.generateReport) {
      downloadFileRef.current.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURI(
        generateReportData.generateReport
      )}`;
      downloadFileRef.current.download = `bitumio-${kebabCase(requestData.type)}-report.xlsx`;
      downloadFileRef.current.click();
    }
  }, [generateReportData]);

  const changeDate = async (selectedDate: Array<Date>, type) => {
    const formattedDate = dayjs(selectedDate[0]).format("YYYY-MM-DD 00:00:00");
    setDate((date) => ({ ...date, [type]: formattedDate }));
  };

  const onSubmit = (values) => {
    const variables = { ...values, ...date };
    setRequestData(variables);
    getReportTimeEntries({ variables });
  };

  const handleDownload = (e) => {
    const variables = { type: ReportType.TIME_ENTRIES, ...date };
    setRequestData(variables);
    generateReport({ variables });
  };

  // Format dates for display
  const formatDisplayDate = (dateString: string) => {
    return dayjs(dateString).format("MMM D, YYYY");
  };

  return (
    <DashboardPage>
      <MDBox mb={6} component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3} alignItems="center" display="flex" justifyContent="center">
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Card>
              <MDBox
                p={3}
                lineHeight={1}
                display="flex"
                alignItems="center"
                width="100%"
                justifyContent="space-between"
              >
                <Box>
                  <MDTypography variant="h4" fontWeight="medium">
                    {capitalCase(ReportType.TIME_ENTRIES)} Report
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mt={1}>
                    Track and analyze all time entries across your organization
                  </MDTypography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "info.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon sx={{ color: "#fff" }}>schedule</Icon>
                </Box>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" mb={2}>
                  Filter Options
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4.5} lg={4.5}>
                    <MDBox
                      height="100%"
                      display="flex"
                      flexDirection="column"
                      justifyContent="space-between"
                    >
                      <MDTypography variant="body2" fontWeight="regular" mb={1}>
                        Start Date
                      </MDTypography>
                      <MDDatePicker
                        defaultValue={today}
                        onChange={(val) => changeDate(val, "startDate")}
                        fullWidth
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={5} lg={5}>
                    <MDBox
                      height="100%"
                      display="flex"
                      flexDirection="column"
                      justifyContent="space-between"
                    >
                      <MDTypography variant="body2" fontWeight="regular" mb={1}>
                        End Date
                      </MDTypography>
                      <MDDatePicker
                        defaultValue={today}
                        onChange={(val) => changeDate(val, "endDate")}
                        fullWidth
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={6} md={1.5} lg={1.5}>
                    <MDBox
                      height="100%"
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                    >
                      <a ref={downloadFileRef} className="d-none" />
                      <MDButton color="success" type="submit" fullWidth sx={{ height: "40px" }}>
                        RUN REPORT
                      </MDButton>
                    </MDBox>
                  </Grid>
                  <Grid item xs={6} md={1} lg={1}>
                    <MDBox
                      height="100%"
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                    >
                      <MDButton
                        color="info"
                        onClick={handleDownload}
                        fullWidth
                        sx={{ height: "40px" }}
                      >
                        <Icon fontSize="small" sx={{ mr: 0.5 }}>
                          download
                        </Icon>
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Grid container alignItems="center">
            <Grid item xs>
              <MDTypography variant="h6" color="text">
                Report Results
              </MDTypography>
            </Grid>
            <Grid item>
              <MDTypography variant="body2" color="text">
                {date.startDate && date.endDate ? (
                  <>
                    Period: {formatDisplayDate(date.startDate)} - {formatDisplayDate(date.endDate)}
                  </>
                ) : (
                  "Select date range"
                )}
              </MDTypography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 1.5 }} />
        </Box>

        <Grid container display="flex">
          <DataTable
            canSearch={false}
            table={paginatedTableData}
            showTotalEntries={true}
            isSorted={true}
            pagination={pagination}
            paginationProps={{ variant: "gradient", color: "info" }}
            entriesPerPageOptions={["10", "15", "25", "50"]}
          />
          {paginatedTableData?.modal}
        </Grid>
      </MDBox>
    </DashboardPage>
  );
};

export default ReportTimeEntries;
